/**
 * Wave 2 — Deposit shapes (spec §4 / plan PR 3).
 *
 * Mirrors the operations service response shape from
 * `apps/operations/src/modules/sales/deposits/deposits.controller.ts`.
 *
 * Deposit-related enums (`DepositStatus`, `DepositType`) live in
 * `lib/api/types/billing.ts` (Wave 1 source of truth).
 */
import type { DepositStatus, DepositType } from '@/lib/api/types/billing';
import type { StaffChargeAuthorization } from '@/types/contact';
import { collectedTotal } from '@/lib/payments/collectedAmount';

/**
 * Held credit still available on a deposit. Only money actually on file
 * (HELD / PARTIALLY_APPLIED / APPLIED) can have credit left; every other status
 * holds $0 regardless of `amountApplied`. A refunded deposit often has
 * `amountApplied = 0` yet nothing is left to spend, a CAPTURE_PENDING one is
 * still clearing, and a CAPTURE_FAILED one never arrived. An allowlist, the
 * same one the operations service uses to refuse applying a deposit, so a
 * status added later fails closed. Single source of truth for the split list,
 * table, and detail panel.
 */
export function depositRemaining(deposit: {
  amount: number;
  amountApplied: number;
  status: DepositStatus;
}): number {
  const onFile =
    deposit.status === 'HELD' ||
    deposit.status === 'PARTIALLY_APPLIED' ||
    deposit.status === 'APPLIED';
  return onFile ? Math.max(0, Number(deposit.amount ?? 0) - Number(deposit.amountApplied ?? 0)) : 0;
}

/**
 * Statuses a deposit can only reach after its capture actually took the money,
 * per the operations service: HELD / PARTIALLY_APPLIED / APPLIED are money on
 * file; REFUND_PENDING is reserved only from HELD / PARTIALLY_APPLIED
 * (payment-refunds.service.ts reserveDepositRefund); REFUNDED is written only
 * from REFUND_PENDING or an unwound HELD / PARTIALLY_APPLIED / APPLIED capture;
 * FORFEITED only from HELD / PARTIALLY_APPLIED. The money was collected before
 * it went back or was kept, so those still report it. CAPTURE_PENDING is still
 * clearing and CAPTURE_FAILED never arrived.
 */
const COLLECTED_STATUSES: ReadonlySet<string> = new Set<DepositStatus>([
  'HELD',
  'PARTIALLY_APPLIED',
  'APPLIED',
  'REFUND_PENDING',
  'REFUNDED',
  'FORFEITED',
]);

/**
 * What the capture actually took off the customer: the net `amount` plus any
 * card service fee (server `chargedAmount`, or the arithmetic for an older
 * cached row). $0 unless the capture happened. An allowlist, so a status
 * added later fails closed rather than claiming money that never arrived.
 */
/** Whether a deposit in this status ever had its money arrive. */
export function isDepositCollected(status: DepositStatus): boolean {
  return COLLECTED_STATUSES.has(status);
}

export function depositCollected(deposit: {
  amount: number;
  status: DepositStatus;
  chargedAmount?: number;
  serviceFeeAmount?: number;
  captureFailed?: boolean;
}): number {
  if (!isDepositCollected(deposit.status)) return 0;
  // A bounced or charged-back capture is stored as REFUNDED, but the money
  // never stayed with the business: nothing was collected.
  if (deposit.captureFailed) return 0;
  return Number(deposit.chargedAmount ?? collectedTotal(deposit));
}

export interface DepositApplication {
  id: string;
  tenantId: string;
  depositId: string;
  invoiceId: string;
  amount: number;
  appliedAt: string;
  reversedAt: string | null;
  reversedBy: string | null;
  reversedReason: string | null;
  invoice?: {
    id: string;
    invoiceNumber: string;
    status: string;
    total: number;
  };
}

export interface Deposit {
  id: string;
  depositNumber: string;
  tenantId: string;
  contactId: string;
  contact?: {
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
  } | null;
  jobId: string | null;
  estimateId: string | null;
  type: DepositType;
  amount: number;
  amountApplied: number;
  status: DepositStatus;
  capturedAt: string;
  capturedViaPaymentId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  applications?: DepositApplication[];
  /**
   * `amount` is always the NET deposit; when the capture payment carried a card
   * service fee (`Payment.surchargeAmount`), the customer was actually charged
   * `chargedAmount = amount + serviceFeeAmount`.
   *
   * Served by both the list and the detail endpoints. Still optional because a
   * response cached from before they were added carries neither — read them
   * through a fallback rather than assuming they are present.
   */
  serviceFeeAmount?: number;
  chargedAmount?: number;
  /** The rail its capture ran on; a pending one reads "Pending ACH" only when BANK. */
  captureRail?: 'BANK' | 'CARD' | null;
  /**
   * Its capture bounced (a bank return) or was charged back. Such a deposit is
   * closed REFUNDED, but no refund was given, so it reads "Capture failed".
   */
  captureFailed?: boolean;
  /**
   * Set on a capture sent with `remainderToJob`: the job payment that took
   * the rest of the money.
   */
  remainderPaymentId?: string;
}

export interface DepositListResponse {
  data: Deposit[];
  total: number;
  page: number;
  limit: number;
}

export interface DepositListParams {
  contactId?: string;
  jobId?: string;
  estimateId?: string;
  search?: string;
  status?: DepositStatus;
  type?: DepositType;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'capturedAt' | 'amount';
  sortDir?: 'asc' | 'desc';
}

export interface CaptureDepositInput {
  paymentMethodDiscountGrossAmount?: number;
  contactId: string;
  jobId?: string;
  estimateId?: string;
  type: DepositType;
  amount: number;
  paymentMethod: string;
  savedPaymentMethodId?: string;
  /**
   * Required when the saved method is a bank account: the customer's
   * authorization for this debit. The server refuses a bank deposit without it.
   */
  achAuthorization?: StaffChargeAuthorization;
  idempotencyKey?: string;
  notes?: string;
  transactionId?: string;
  methodDetails?: Record<string, unknown>;
  /**
   * A job's own deposit taken by hand for more than the deposit due: the
   * server holds the deposit due as the deposit and records the rest as a job
   * payment, both in one step. Refused when the amount is not more than due.
   */
  remainderToJob?: boolean;
}

export interface ApplyDepositInput {
  invoiceId: string;
  amount?: number;
}

export interface RefundDepositInput {
  reason: string;
}

export interface ForfeitDepositInput {
  reason: string;
}

export interface ApplyDepositResponse {
  deposit: Deposit;
  application: DepositApplication;
}

export interface RefundDepositResponse {
  deposit: Deposit;
  creditMemoId: string;
  gatewayRefundId: string | null;
}

export interface ContactDepositBalance {
  contactId: string;
  heldAmount: number;
  deposits: Array<{
    id: string;
    depositNumber: string;
    type: DepositType;
    amount: number;
    amountApplied: number;
    remaining: number;
    status: DepositStatus;
  }>;
}

/** Spec §4.4 / §4.3 — error codes returned by the operations service. */
export const DepositErrorCode = {
  OVERAPPLIED: 'DEPOSIT_OVERAPPLIED',
  ALREADY_APPLIED: 'DEPOSIT_ALREADY_APPLIED',
  ILLEGAL_STATE: 'DEPOSIT_ILLEGAL_STATE',
  NOT_HELD: 'DEPOSIT_NOT_HELD',
  ZERO_AMOUNT: 'DEPOSIT_ZERO_AMOUNT',
} as const;
export type DepositErrorCode = (typeof DepositErrorCode)[keyof typeof DepositErrorCode];
